import useMarketplaceFilters, { readSavedUserLocation } from "../hooks/useMarketplaceFilters";
import { toast } from "../hooks/use-toast";
import { useState } from "react";
import { Globe, MapPin, Locate, LocateFixed, RotateCcw, ChevronDown } from "lucide-react";

const languages = ["English", "Swahili", "French", "Arabic", "Luganda"];

const countries = [
  { name: "Uganda", cities: ["Kampala", "Entebbe", "Jinja", "Mukono", "Mbarara", "Gulu"] },
  { name: "Kenya", cities: ["Nairobi", "Mombasa", "Kisumu"] },
  { name: "Tanzania", cities: ["Dar es Salaam", "Mwanza", "Arusha"] },
  { name: "Rwanda", cities: ["Kigali", "Musanze"] },
  { name: "Nigeria", cities: ["Lagos", "Abuja", "Kano"] },
];

const FilterSelect = ({ icon: Icon, ariaLabel, value, onChange, children }) => (
  <div className="group relative flex shrink-0 items-center">
    <Icon className="pointer-events-none absolute left-2.5 h-4 w-4 text-primary" />
    <select
      value={value}
      onChange={onChange}
      aria-label={ariaLabel}
      className="appearance-none rounded-full border border-border bg-card py-2 pl-8 pr-7 text-sm text-foreground shadow-sm transition-colors hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 group-focus-within:rotate-180" />
  </div>
);

const FilterBar = () => {
  const { filters, updateFilters, resetFilters, hasLocationFilter } = useMarketplaceFilters();

  const selectedCountry = countries.find((entry) => entry.name === filters.country);

  const handleNearMe = () => {
    if (filters.nearMe) {
      updateFilters({ nearMe: false, country: "", city: "" });
      return;
    }

    const { country, city } = readSavedUserLocation();

    if (!country && !city) {
      toast({
        title: "Location needed",
        description: "Add your country and city in Profile to use Near Me.",
        duration: 3500,
      });
      return;
    }

    updateFilters({ nearMe: true, country, city });
  };

  return (
    <div className="border-b border-border bg-secondary/50">
      <style>{`
        @keyframes fb-pop { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        .fb-pop { animation: fb-pop 0.25s cubic-bezier(0.16,1,0.3,1); }
        @media (prefers-reduced-motion: reduce) {
          .fb-pop { animation: none; }
        }
      `}</style>

      <div className="container mx-auto flex flex-wrap items-center gap-2 px-4 py-2">
        <FilterSelect
          icon={Globe}
          ariaLabel="Language preference"
          value={filters.language}
          onChange={(event) => updateFilters({ language: event.target.value })}
        >
          {languages.map((language) => (
            <option key={language} value={language}>
              {language}
            </option>
          ))}
        </FilterSelect>

        <span className="hidden h-6 w-px shrink-0 bg-border sm:block" />

        <FilterSelect
          icon={MapPin}
          ariaLabel="Filter by country"
          value={filters.country}
          onChange={(event) =>
            updateFilters({ country: event.target.value, city: "", nearMe: false })
          }
        >
          <option value="">All Countries</option>
          {countries.map((country) => (
            <option key={country.name} value={country.name}>
              {country.name}
            </option>
          ))}
        </FilterSelect>

        {selectedCountry && (
          <div className="fb-pop shrink-0">
            <FilterSelect
              icon={MapPin}
              ariaLabel="Filter by city"
              value={filters.city}
              onChange={(event) => updateFilters({ city: event.target.value, nearMe: false })}
            >
              <option value="">All Cities</option>
              {selectedCountry.cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </FilterSelect>
          </div>
        )}

        <button
          type="button"
          onClick={handleNearMe}
          aria-pressed={filters.nearMe}
          className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
            filters.nearMe
              ? "bg-primary text-primary-foreground shadow-sm shadow-primary/30"
              : "border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
          }`}
        >
          {filters.nearMe ? (
            <LocateFixed className="h-3.5 w-3.5" />
          ) : (
            <Locate className="h-3.5 w-3.5" />
          )}
          Near Me
        </button>

        {hasLocationFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="fb-pop flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-medium text-muted-foreground transition-all duration-300 hover:-translate-y-0.5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear location
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;